import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { debounceTime, distinctUntilChanged, finalize, Subject, takeUntil } from 'rxjs';
import { HomologationItem, HomologationSaveEvent } from '../services/homologations.interfaces';
import { HomologationsService } from '../services/homologations.service';
import { DatePipe } from '@angular/common';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BreadcrumbNavigationService } from '@angular-monorepo/ui';
import { Breadcrumb } from 'primeng/breadcrumb';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { HomologationFormComponent } from '../components/modals/homologation-form.component';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Tooltip } from 'primeng/tooltip';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'lib-homologation',
  standalone: true,
  imports: [
    Breadcrumb,
    TranslatePipe,
    Button,
    InputGroup,
    InputGroupAddon,
    FormsModule,
    InputText,
    DatePipe,
    ConfirmDialog,
    TableModule,
    Tag,
    HomologationFormComponent,
    ProgressSpinner,
    Tooltip,
    RouterLink,
  ],
  templateUrl: './homologation.component.html',
  styleUrl: './homologation.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class HomologationComponent implements OnInit, OnDestroy {
  @Input() showBreadcrumb = true;
  @Input() showTitle = true;

  homologations: HomologationItem[] = [];
  loading = false;
  readonly = false;

  totalRecords = 0;
  rows = 10;
  first = 0;
  currentPage = 1;

  showFormModal = false;
  homologationToEdit: HomologationItem | null = null;
  loadingDetail = false;

  searchTerm = '';
  private readonly destroy$ = new Subject<void>();
  private readonly search$ = new Subject<string>();

  private readonly homologationService = inject(HomologationsService);
  private readonly translate = inject(TranslateService);
  private readonly breadcrumbNav = inject(BreadcrumbNavigationService);
  public homologationBreadcrumbs = computed(() =>
    this.breadcrumbNav.generateBreadcrumbs('homologations'),
  );
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.setupSearch();
    this.loadHomologations();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getRulesLabel(h: HomologationItem): string {
    const count = h.json_data.length;
    return `${count} ${count === 1 ? 'regla' : 'reglas'}`;
  }

  loadHomologations(): void {
    this.loading = true;
    this.cdr.markForCheck();

    this.homologationService
      .getAllHomologations(this.currentPage, this.rows, this.searchTerm)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          if (!response || !Array.isArray(response.data)) {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('common.error'),
              detail: this.extractResponseMessage(
                response,
                this.translate.instant('homologationModule.errors.load'),
              ),
            });
            this.cdr.markForCheck();
            return;
          }

          this.homologations = response.data.map((h) => ({
            ...h,
            json_data: this.normalizeJsonData(h.json_data),
          }));
          this.totalRecords = response.total;
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.error'),
            detail: this.extractErrorMessage(
              error,
              this.translate.instant('homologationModule.errors.load'),
            ),
          });
          this.cdr.markForCheck();
        },
      });
  }

  onPageChange(event: { first: number; rows: number }): void {
    this.first = event.first;
    this.rows = event.rows;

    this.currentPage = Math.floor(this.first / this.rows) + 1;

    this.loadHomologations();
  }

  onView(item: HomologationItem): void {
    this.loadingDetail = true;

    this.homologationService
      .getHomologationById(item.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (fullItem) => {
          this.homologationToEdit = {
            ...fullItem,
            json_data: Array.isArray(fullItem.json_data)
              ? fullItem.json_data
              : [fullItem.json_data],
          };

          this.readonly = true;
          this.showFormModal = true;
          this.loadingDetail = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loadingDetail = false;
        },
      });
  }

  onEdit(h: HomologationItem): void {
    this.readonly = false;
    this.homologationToEdit = h;
    this.showFormModal = true;
    this.cdr.markForCheck();
  }

  onNewHomologation(): void {
    this.homologationToEdit = null;
    this.readonly = false;
    this.showFormModal = true;
    this.cdr.markForCheck();
  }

  onSaveHomologation(event: HomologationSaveEvent): void {
    let request$;

    if (event.mode === 'import') {
      const { file, name } = event.payload;

      request$ = event.id
        ? this.homologationService.importUpdateHomologationById(event.id, file, name)
        : this.homologationService.importCreateHomologation(file, name);
    } else {
      const { name, json_data } = event.payload;

      request$ = event.id
        ? this.homologationService.updateHomologationById(event.id, {
            name,
            json_data,
          })
        : this.homologationService.createManualHomologation({
            name,
            json_data,
          });
    }

    this.loading = true;
    this.cdr.markForCheck();

    request$
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.showFormModal = false;
          this.homologationToEdit = null;
          this.resetPagination();

          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.success'),
            detail: this.translate.instant('homologationModule.success.saved'),
          });

          this.loadHomologations();
        },
        error: (err: unknown) => {
          let detail = this.translate.instant('homologationModule.errors.generic');

          if (typeof err === 'object' && err !== null) {
            const httpError = err as {
              error?: {
                error?: string;
                message?: string;
                detail?: string;
              };
            };

            if (httpError.error?.error === 'file error') {
              detail = this.translate.instant('homologationModule.errors.invalidFile');
            } else {
              detail = this.extractErrorMessage(err, detail);
            }
          }

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail,
          });

          this.cdr.markForCheck();
        },
      });
  }

  onDelete(h: HomologationItem): void {
    this.confirmationService.confirm({
      header: this.translate.instant('homologationModule.confirm.deleteTitle'),
      message: this.translate.instant('homologationModule.confirm.deleteMessage', { name: h.name }),
      icon: 'pi pi-exclamation-triangle',

      acceptLabel: this.translate.instant('common.yes'),
      rejectLabel: this.translate.instant('common.no'),

      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',

      accept: () => {
        this.homologationService
          .deleteHomologationById(h.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.resetPagination();
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('common.success'),
              detail: this.translate.instant('homologationModule.success.deleted'),
            });

            this.loadHomologations();
          });
      },
    });
  }

  onCancelHomologation(): void {
    this.showFormModal = false;
    this.readonly = false;
    this.homologationToEdit = null;
    this.cdr.markForCheck();
  }

  onSearchChange(): void {
    this.search$.next(this.searchTerm);
  }

  onClearSearch(): void {
    this.searchTerm = '';
    this.onSearchChange();
  }

  onRefresh(): void {
    this.resetPagination();
    this.loadHomologations();
  }

  private setupSearch(): void {
    this.search$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.resetPagination();
        this.loadHomologations();
      });
  }

  private resetPagination(): void {
    this.first = 0;
    this.currentPage = 1;
  }

  private normalizeJsonData(data: unknown): HomologationItem['json_data'] {
    if (Array.isArray(data)) {
      return data;
    }

    if (data && typeof data === 'object') {
      return [data as HomologationItem['json_data'][number]];
    }

    return [];
  }

  private extractErrorMessage(error: unknown, fallback: string): string {
    if (typeof error === 'object' && error !== null) {
      const httpError = error as {
        error?: {
          error?: string;
          message?: string;
          detail?: string;
        };
        message?: string;
      };

      return (
        httpError.error?.error ||
        httpError.error?.message ||
        httpError.error?.detail ||
        httpError.message ||
        fallback
      );
    }

    return fallback;
  }

  private extractResponseMessage(response: unknown, fallback: string): string {
    if (typeof response === 'object' && response !== null) {
      const payload = response as {
        error?: string;
        message?: string;
        detail?: string;
      };

      return payload.error || payload.message || payload.detail || fallback;
    }

    return fallback;
  }
}
